// Web Vitals → Sentry — Round C extensión 2026-04-29.
//
// Por qué existe esto si Sentry ya captura vitals via
// `browserTracingIntegration`?
//   La integración auto-instrumenta cada pageload y adjunta CLS/LCP/FID
//   como measurements, lo cual sirve para AVERAGES en el dashboard de
//   Performance. Pero NO genera "issues" individuales — entonces no
//   podés crear una alerta tipo "avísame cuando un usuario tuvo LCP
//   peor que 4s". Para cubrir ese caso, este reporter envía un evento
//   discreto a Sentry SOLO cuando un vital cae en `rating: "poor"`.
//
// Threshold por vital (Google Web Vitals 2024):
//   - LCP poor   > 4000 ms     (largest contentful paint)
//   - INP poor   > 500 ms      (interaction to next paint, reemplaza FID)
//   - CLS poor   > 0.25        (cumulative layout shift, sin unidad)
//   - FCP poor   > 3000 ms     (first contentful paint)
//   - TTFB poor  > 1800 ms     (time to first byte)
//
// El SDK web-vitals calcula el rating internamente — solo leemos
// `metric.rating` y filtramos `=== "poor"`.

import * as Sentry from "@sentry/react";

type Metric = {
  name: string;
  value: number;
  delta: number;
  id: string;
  rating: "good" | "needs-improvement" | "poor";
  navigationType?: string;
};

const handleMetric = (metric: Metric): void => {
  // Solo elevamos a evento las experiencias poor — el resto ya viaja
  // como measurement via browserTracingIntegration. Si subiéramos
  // todos, se duplicarían y saturarían el cuota gratuita de Sentry.
  if (metric.rating !== "poor") return;

  // captureMessage en lugar de captureException porque NO es un error
  // del código — es una métrica de UX. Level "warning" para que entre
  // a Issues pero no dispare alertas críticas.
  Sentry.captureMessage(`Web Vital poor: ${metric.name}`, {
    level: "warning",
    tags: {
      "webvital.name": metric.name,
      "webvital.rating": metric.rating,
      // route en la que se produjo — ayuda a localizar la página lenta.
      "webvital.route":
        typeof window !== "undefined" ? window.location.pathname : "unknown",
    },
    extra: {
      value: metric.value,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
    },
  });
};

/**
 * Activa la captura de Web Vitals → Sentry para experiencias "poor".
 * Llamar una vez al boot del frontend (en `index.tsx`).
 *
 * Es noop si Sentry no está inicializado (DSN ausente) — captureMessage
 * sin DSN es internamente un no-op del SDK.
 */
export default function reportWebVitals(): void {
  // Import dinámico para que el chunk de web-vitals no bloquee el
  // primer render. Con CRA 5 el bundle se code-splittea automático.
  import("web-vitals")
    .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      onCLS(handleMetric);
      onFCP(handleMetric);
      onINP(handleMetric);
      onLCP(handleMetric);
      onTTFB(handleMetric);
    })
    .catch(() => {
      // Si web-vitals no se puede cargar (red caída, bundle corrupto),
      // ignoramos en silencio. El usuario ya tiene problemas peores
      // que la falta de telemetría.
    });
}
